import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { uploadCover } from "@/lib/events";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CoverImagePicker({ value, onChange, testidPrefix = "cover" }) {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!user?.id) {
      toast.error("Log in first.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadCover(file, user.id);
      onChange(url);
    } catch (err) {
      toast.error(fmtError(err) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`${testidPrefix}-picker`}>
      {value ? (
        <div className="relative border border-ink/15 group">
          <img
            src={value}
            alt="cover"
            className="w-full max-h-72 object-cover"
            loading="lazy"
          />
          <button
            type="button"
            data-testid={`${testidPrefix}-remove`}
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 bg-paper/80 border border-ink/15 hover:border-neon hover:text-ink p-2 text-ink transition-colors"
            aria-label="Remove image"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testidPrefix}-upload-trigger`}
          className="w-full border-2 border-dashed border-ink/15 hover:border-neon p-8 flex flex-col items-center justify-center gap-2 text-ink/60 hover:text-ink transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <>
              <Loader2 size={20} strokeWidth={1.5} className="animate-spin" />
              <span className="text-xs uppercase tracking-widest">Uploading…</span>
            </>
          ) : (
            <>
              <Upload size={20} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest">Upload cover image (≤ 5 MB)</span>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid={`${testidPrefix}-file-input`}
      />
    </div>
  );
}
